// Read-only, fixed-stake historical research benchmark. Never uses actual trade stakes.
// The bucket-bets endpoint is the single source of selection history and BSP grading.
export type HypotheticalBucketRow = {
  date?: string | null;
  start_time?: string | null;
  policy_id?: string | null;
  track?: string | null;
  race?: number | string | null;
  runner?: string | null;
  market_type?: string | null;
  side?: string | null;
  result?: string | null;
  bsp?: number | null;
  dead_heat_winners?: number | null;
  retrospective?: boolean;
  research_backtest_kind?: string | null;
  research_forward_only?: boolean;
  placed?: boolean;
  first_recorded_at?: string | null;
};

export type FortnightPortfolio = {
  bankStart: number;
  bankEnd: number;
  netProfit: number;
  bankReturnPct: number;
  riskReturnPct: number;
  riskTurnover: number;
  maxDrawdownPct: number;
  winningSelections: number;
  settledSelections: number;
  backBets: number;
  layBets: number;
  duplicatesRemoved: number;
  conflictingRows: number;
  retrospectiveIncluded: number;
  forwardIncluded: number;
  missingStartTime: number;
  unpriced: number;
  pending: number;
  voided: number;
  unfunded: number;
  sourceRows: number;
  distinctCandidates: number;
  policyCount: number;
};

export const FORTNIGHT_START_BANK = 5000;
export const FORTNIGHT_BACK_STAKE = 100;
export const FORTNIGHT_LAY_LIABILITY = 300;
export const FORTNIGHT_COMMISSION = 0.10;

// Date boundaries use Australia/Sydney, not the browser or server timezone.
// This is a 14-calendar-day window including the current Sydney date.
export function fortnightDates(now: Date = new Date()): {from: string; to: string} {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(now);
  const part = (type: string) => parts.find(p => p.type === type)?.value || '';
  const to = [part('year'), part('month'), part('day')].join('-');
  const start = new Date(Date.UTC(Number(part('year')), Number(part('month')) - 1, Number(part('day')) - 13));
  return {from: start.toISOString().slice(0, 10), to};
}

const norm = (value: unknown): string =>
  String(value || '').toUpperCase().replace(/^\d+[.]?\s*/, '').replace(/[^A-Z0-9]/g, '');

const direction = (r: HypotheticalBucketRow): 'back' | 'lay' =>
  String(r.side || 'back').toLowerCase() === 'lay' ? 'lay' : 'back';

const retrospective = (r: HypotheticalBucketRow): boolean =>
  r.retrospective === true || Boolean(r.research_backtest_kind);

const verifiedForward = (r: HypotheticalBucketRow): boolean =>
  !retrospective(r) && (r.research_forward_only === true || r.placed === true ||
    (Number.isFinite(Date.parse(r.first_recorded_at || '')) &&
      Number.isFinite(Date.parse(r.start_time || '')) &&
      Date.parse(r.first_recorded_at || '') <= Date.parse(r.start_time || '')));

const quality = (r: HypotheticalBucketRow): number =>
  Number(r.placed === true) * 8 + Number(verifiedForward(r)) * 4 +
  Number(!retrospective(r)) * 2 + Number(Number(r.bsp) > 1);

const isSettled = (r: HypotheticalBucketRow) => r.result === 'win' || r.result === 'lose';

const identity = (r: HypotheticalBucketRow): string =>
  [r.date, norm(r.track), Number(r.race), norm(r.runner), direction(r),
   String(r.market_type || 'WIN').toUpperCase()].join('|');

function conflicting(a: HypotheticalBucketRow, b: HypotheticalBucketRow): boolean {
  if (isSettled(a) && isSettled(b) && a.result !== b.result) return true;
  const ap = Number(a.bsp), bp = Number(b.bsp);
  return isSettled(a) && isSettled(b) && ap > 1 && bp > 1 && Math.abs(ap - bp) > 0.10;
}

// The bank is revalued at the race start using the authoritative BSP outcome.
// This is a *hypothetical* fixed-risk sequence, not an available-cash or matched-order
// replay. Simultaneous races use a stable order, and actual settlement delays are
// not simulated. If the calculated bank falls below a required stake/liability,
// that selection is not funded.
export function calculateFortnightPortfolio(
  rows: HypotheticalBucketRow[],
  window: {from: string; to: string}
): FortnightPortfolio {
  const relevant = (rows || []).filter(r =>
    typeof r.date === 'string' && r.date >= window.from && r.date <= window.to &&
    Boolean(r.policy_id) && Boolean(norm(r.track)) && Number(r.race) > 0 && Boolean(norm(r.runner))
  );
  const grouped = new Map<string, HypotheticalBucketRow>();
  const conflicts = new Set<string>();
  let duplicatesRemoved = 0;
  for (const row of relevant) {
    const key = identity(row);
    const previous = grouped.get(key);
    if (!previous) { grouped.set(key, row); continue; }
    duplicatesRemoved++;
    if (conflicting(previous, row)) conflicts.add(key);
    if (quality(row) > quality(previous)) grouped.set(key, row);
  }

  const distinct = [...grouped.entries()]
    .filter(([key]) => !conflicts.has(key))
    .map(([, row]) => row)
    .sort((a, b) => Date.parse(a.start_time || '') - Date.parse(b.start_time || '') ||
      identity(a).localeCompare(identity(b)));

  let bank = FORTNIGHT_START_BANK;
  let peak = bank;
  let maxDrawdownPct = 0;
  let riskTurnover = 0;
  let backBets = 0;
  let layBets = 0;
  let winningSelections = 0;
  let retrospectiveIncluded = 0;
  let forwardIncluded = 0;
  let missingStartTime = 0;
  let unpriced = 0;
  let pending = 0;
  let voided = 0;
  let unfunded = 0;

  for (const row of distinct) {
    if (row.result === 'void') { voided++; continue; }
    if (!isSettled(row)) { pending++; continue; }
    const bsp = Number(row.bsp);
    if (!(bsp > 1 && bsp <= 1000)) { unpriced++; continue; }
    if (!Number.isFinite(Date.parse(row.start_time || ''))) { missingStartTime++; continue; }
    const lay = direction(row) === 'lay';
    const risk = lay ? FORTNIGHT_LAY_LIABILITY : FORTNIGHT_BACK_STAKE;
    if (bank < risk) { unfunded++; continue; }

    // A winning lay means the runner lost. Its *maximum liability* is $300.
    // Commission is deducted only from positive gross winnings, including
    // short-priced/lay wins. A loss is always limited to the modelled risk.
    const deadHeat = Number(row.dead_heat_winners) > 1
      ? Number(row.dead_heat_winners) : 1;
    if (lay && deadHeat > 1) { unpriced++; continue; } // cannot infer lay dead-heat settlement
    const gross = row.result === 'lose' ? -risk
      : lay ? risk / (bsp - 1)
      : risk * (bsp / deadHeat - 1);
    const net = gross > 0 ? gross * (1 - FORTNIGHT_COMMISSION) : gross;

    bank += net;
    riskTurnover += risk;
    backBets += Number(!lay);
    layBets += Number(lay);
    winningSelections += Number(row.result === 'win');
    retrospectiveIncluded += Number(retrospective(row));
    forwardIncluded += Number(verifiedForward(row));
    peak = Math.max(peak, bank);
    if (peak > 0) maxDrawdownPct = Math.max(maxDrawdownPct, 100 * (peak - bank) / peak);
  }
  const settledSelections = backBets + layBets;
  const netProfit = bank - FORTNIGHT_START_BANK;
  return {
    bankStart: FORTNIGHT_START_BANK, bankEnd: bank, netProfit,
    bankReturnPct: netProfit / FORTNIGHT_START_BANK * 100,
    riskReturnPct: riskTurnover ? netProfit / riskTurnover * 100 : 0,
    riskTurnover, maxDrawdownPct, winningSelections, settledSelections, backBets, layBets,
    duplicatesRemoved, conflictingRows: conflicts.size, retrospectiveIncluded, forwardIncluded,
    missingStartTime, unpriced, pending, voided, unfunded,
    sourceRows: relevant.length, distinctCandidates: grouped.size,
    policyCount: new Set(relevant.map(r => r.policy_id)).size
  };
}
